/**
 * One background schedule — when a job runs, and what those runs did.
 *
 * There is no GET /schedules/{id}, so the page carries `?jobId=` and finds the
 * schedule among its job's schedules, exactly as the edit form does. Without the
 * hint it searches every job's.
 */
import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'sharedUi/Tag';
import { Button } from 'sharedUi/Button';
import { Message } from 'sharedUi/Message';
import { InputSwitch } from 'sharedUi/InputSwitch';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useToastApi } from '../../hooks/use-toast-api';
import { backgroundJobService } from '../../services/background-job.service';
import { JsonView } from '../../components/common/json-view';
import {
  CARD, BackLink, CardHeader, Field, Hero, ActiveBanner, WarnBanner, RecordMeta,
} from '../../components/notifications/engine-view-ui';
import { POLL_INTERVAL_SECONDS, describeTiming, fromNaiveIso } from './tabs/shared';
import { useJobPermissions } from './tabs/use-job-permissions';
import { groupRuns } from './run-format';
import { RunsCard } from './runs-card';
import { jobPath } from './view-paths';

const SCHEDULE_LIST = '/background-jobs?tab=schedules';

const BackgroundScheduleView = () => {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const hintedJobId = params.get('jobId') || '';
  const navigate = useAppNavigate();
  const qc = useQueryClient();
  const { toast, ok, fail } = useToastApi();
  const { canUpdate, canToggle, canReadRuns } = useJobPermissions();

  // Only needed to search every job when the URL did not say which one owns it.
  const jobsQ = useQuery({
    queryKey: ['bg-jobs', 'all-for-schedules'],
    queryFn: () => backgroundJobService.listJobs({ page: 1, page_size: 200 }),
    enabled: !hintedJobId,
    staleTime: 60_000,
  });
  const searchJobIds = hintedJobId ? [hintedJobId] : (jobsQ.data?.items ?? []).map((j) => j._id);

  const scheduleQs = useQueries({
    queries: searchJobIds.map((jobId) => ({
      queryKey: ['bg-schedules', jobId],
      queryFn: () => backgroundJobService.listSchedules(jobId),
      staleTime: 30_000,
    })),
  });
  const schedule = scheduleQs.flatMap((q) => q.data ?? []).find((s) => s._id === id);
  const searching = (!hintedJobId && jobsQ.isLoading) || scheduleQs.some((q) => q.isLoading);

  const jobQ = useQuery({
    queryKey: ['bg-job', schedule?.jobId],
    queryFn: () => backgroundJobService.getJob(schedule!.jobId),
    enabled: !!schedule?.jobId,
    staleTime: 60_000,
  });

  // Keyed on updatedAt so an edit or a toggle recomputes what "next" means.
  const previewQ = useQuery({
    queryKey: ['bg-schedule-preview', id, schedule?.updatedAt, schedule?.isScheduleEnabled],
    queryFn: () => backgroundJobService.previewSchedule(id!, 5),
    enabled: !!schedule,
    staleTime: 30_000,
  });

  const runsQ = useQuery({
    queryKey: ['bg-runs', { jobScheduleId: id, view: 'schedule' }],
    queryFn: () => backgroundJobService.listRuns({
      jobScheduleId: id!, page: 1, page_size: 50, sort_by: 'startedAt', sort_order: 'desc',
    }),
    enabled: !!schedule && canReadRuns,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
  const groups = useMemo(() => groupRuns(runsQ.data?.items ?? []).slice(0, 10), [runsQ.data]);

  const toggleM = useMutation({
    mutationFn: (on: boolean) => backgroundJobService.toggleSchedule(id!, on),
    onSuccess: (_d, on) => {
      ok(on ? 'Enabled — the next run is computed from now, not from the old value' : 'Disabled — history is preserved');
      qc.invalidateQueries({ queryKey: ['bg-schedules'] });
      qc.invalidateQueries({ queryKey: ['bg-schedule-preview'] });
    },
    onError: (e) => fail(e),
  });

  if (searching) {
    return <div className="flex min-h-[300px] items-center justify-center"><ProgressSpinner style={{ width: 40, height: 40 }} /></div>;
  }
  if (!schedule) {
    return (
      <div className="p-4">
        {toast}
        <Message severity="error" text="Schedule not found — it may have been deleted." className="mb-4 w-full" />
        <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={() => navigate(SCHEDULE_LIST)} />
      </div>
    );
  }

  const job = jobQ.data;
  // A ONCE schedule disables itself after firing — "completed", not "turned off".
  const completed = !schedule.isScheduleEnabled && schedule.scheduleType === 'ONCE' && !!schedule.lastRunAt;
  // The poller selects on nextRunAt, so enabled with no next run never fires.
  const wontFire = !completed && schedule.isScheduleEnabled && !schedule.nextRunAt;
  // Due in the past and still enabled: the poller should have taken it and did not.
  const due = fromNaiveIso(schedule.nextRunAt);
  const overdue = schedule.isScheduleEnabled && !!due && due.getTime() < Date.now() - POLL_INTERVAL_SECONDS * 1000;
  const occurrences = previewQ.data?.occurrences ?? [];

  return (
    <div className="mx-auto space-y-3 p-4" data-testid="SCR-BackgroundSchedule-View">
      {toast}
      <BackLink label="Background scheduler" onClick={() => navigate(SCHEDULE_LIST)} />

      <Hero
        icon="calendar"
        chip="bg-indigo-50 text-indigo-500 dark:bg-indigo-950/50 dark:text-indigo-400"
        code={schedule.scheduleName}
        tags={<>
          <Tag value={schedule.scheduleType} severity={schedule.scheduleType === 'CRON' ? 'info' : schedule.scheduleType === 'INTERVAL' ? 'warning' : 'secondary'} />
          {completed
            ? <Tag value="Completed" severity="info" />
            : <Tag value={schedule.isScheduleEnabled ? 'Enabled' : 'Disabled'} severity={schedule.isScheduleEnabled ? 'success' : 'secondary'} />}
          {wontFire && <Tag value="Won't fire" severity="warning" />}
          {overdue && <Tag value="Overdue" severity="danger" />}
        </>}
        description={job
          ? <>Runs <button type="button" onClick={() => navigate(jobPath(job._id))}
              className="font-medium text-blue-600 hover:underline dark:text-blue-400">{job.jobName} ({job.jobKey})</button></>
          : null}
        actions={<>
          <div className="flex items-center gap-2 pr-1"
            title={!canToggle ? 'You do not have permission to enable or disable schedules.'
              : completed ? 'This one-off already ran. Edit it to set a new run time and re-enable it together.'
                : undefined}>
            <span className="text-xs text-ink-muted">Enabled</span>
            <InputSwitch
              checked={!!schedule.isScheduleEnabled}
              disabled={!canToggle || completed || toggleM.isPending}
              onChange={(e) => toggleM.mutate(!!e.value)}
            />
          </div>
          {canUpdate && (
            <Button label="Edit" icon="pi pi-pencil" size="small" severity="secondary"
              onClick={() => navigate(`/background-jobs/schedules/${schedule._id}/edit?jobId=${schedule.jobId}`)} />
          )}
          <Button label="Open job" icon="pi pi-cog" size="small" severity="secondary" outlined
            onClick={() => navigate(jobPath(schedule.jobId))} />
        </>}
        banner={overdue
          ? <WarnBanner text="Due but not run — the job engine is most likely not running (JOB_ENGINE_ENABLED)." />
          : wontFire
            ? <WarnBanner text="Enabled but it has no next run, so it will never fire. A ONCE time in the past clears it — set a future time." />
            : schedule.isScheduleEnabled
              ? <ActiveBanner text="Enabled — the scheduler runs it at each next run time below." />
              : null}
      />

      <div className={CARD}>
        <CardHeader icon="clock" color="text-indigo-400" title="Timing" hint={schedule.timezone || undefined} />
        <div className="grid grid-cols-1 gap-x-6 gap-y-3.5 px-4 py-4 sm:grid-cols-2">
          <Field label="When" mono>{describeTiming(schedule)}</Field>
          <Field label="Next run">{schedule.nextRunAt ? formatTimestamp(schedule.nextRunAt) : '—'}</Field>
          <Field label="Last run">{schedule.lastRunAt ? formatTimestamp(schedule.lastRunAt) : 'Never'}</Field>
          <Field label="Timezone">{schedule.timezone || 'Server default'}</Field>
          <Field label="Starts">{schedule.startDateTime ? formatTimestamp(schedule.startDateTime) : 'Immediately'}</Field>
          <Field label="Ends">{schedule.endDateTime ? formatTimestamp(schedule.endDateTime) : 'Never'}</Field>
          {schedule.lockedBy && <Field label="Locked by" mono>{schedule.lockedBy}</Field>}
        </div>
      </div>

      <div className={CARD}>
        <CardHeader icon="calendar-clock" color="text-sky-400" title="Next occurrences" hint="What the timing actually means" />
        <div className="px-4 py-4">
          {previewQ.isLoading ? (
            <span className="text-sm text-gray-400">Working it out…</span>
          ) : previewQ.isError ? (
            <span className="text-sm text-gray-400">Could not preview this schedule.</span>
          ) : occurrences.length ? (
            <ol className="flex flex-col gap-1">
              {occurrences.map((o, i) => (
                <li key={o} className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="mr-2 inline-block w-4 text-right text-xs text-gray-400">{i + 1}</span>
                  {formatTimestamp(o)}
                </li>
              ))}
              {previewQ.data?.exhausted && (
                <li className="mt-1 text-xs text-gray-400">No more after these — the schedule ends.</li>
              )}
            </ol>
          ) : (
            <span className="text-sm text-gray-400">It will not run again.</span>
          )}
        </div>
      </div>

      <div className={CARD}>
        <CardHeader icon="code" color="text-emerald-400" title="Schedule parameters" hint="Merged over the job's defaults on every scheduled run" />
        <div className="px-4 py-4">
          <JsonView label="Parameters" value={schedule.scheduleParameters} />
        </div>
      </div>

      {canReadRuns ? (
        <RunsCard
          title="Runs from this schedule"
          hint="Latest 10 fires"
          groups={groups}
          loading={runsQ.isLoading}
          empty="This schedule has not run anything yet."
        />
      ) : null}

      <RecordMeta data={schedule} extra={job
        ? <Field label="Job">{job.jobName} ({job.jobKey})</Field>
        : undefined} />
    </div>
  );
};

export default BackgroundScheduleView;
